// import "./Stats.css";
import React, { useState, useEffect } from "react";
import Wallet from "../../assets/wallet.jpg";
import { Chart } from "primereact/chart";
import useUserInventory from "../../hooks/useUserInventory";

function DistributorStats() {
  const { inventory, loading, error } = useUserInventory(); // Use the custom hook
  const [distinctBrands, setDistinctBrands] = useState([]);
  const [distinctCondition, setDistinctCondition] = useState([]);

  useEffect(() => {
    if (inventory.length > 0) {
      // Brandwise stock calculation
      const brandwiseStock = inventory.reduce((acc, item) => {
        const { brand } = item;
        acc[brand] = (acc[brand] || 0) + 1;
        return acc;
      }, {});

      // Condition-wise stock calculation
      const conditionMap = inventory.reduce((acc, item) => {
        const { condition } = item;
        acc[condition] = (acc[condition] || 0) + 1;
        return acc;
      }, {});

      // Update states with calculated data
      setDistinctBrands(
        Object.entries(brandwiseStock).map(([brand, quantity]) => ({
          brand,
          quantity,
        }))
      );
      setDistinctCondition(
        Object.entries(conditionMap).map(([condition, quantity]) => ({
          condition,
          quantity,
        }))
      );
    }
  }, [inventory]);

  if (loading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <p>Error: {error}</p>;
  }

  return (
    <div>
      {/* Summary Section */}
      <div className="w-full h-auto overflow-hidden rounded-lg bg-white text-black">
        <div className="bg-red-500 h-6 flex items-center p-2 text-white">
          Summary
        </div>

        <div className="flex w-full overflow-x-auto p-5">
          <SummaryCard
            key={1}
            logo={Wallet}
            title={"Total Products"}
            num={inventory.length}
            color={"linear-gradient(to top, red, white)"}
          />
          <SummaryCard
            key={2}
            logo={Wallet}
            title={"Total Brands"}
            num={distinctBrands.length}
            color={"linear-gradient(to bottom, red, white)"}
          />
          {distinctBrands.length > 0 &&
            distinctBrands.map(({ brand, quantity }, index) => (
              <SummaryCard
                key={index}
                logo={Wallet}
                title={brand}
                num={quantity}
                color={"linear-gradient(to right, #02aab0, #00cdac)"}
              />
            ))}
        </div>
      </div>

      {/* Stock Section */}
      <div className="flex justify-between">
        {distinctBrands.length > 0 && distinctCondition.length > 0 && (
          <StockCharts
            distinct={distinctBrands}
            conditionDistinct={distinctCondition}
          />
        )}
      </div>
    </div>
  );
}

export default DistributorStats;

function StockCharts({ distinct, conditionDistinct }) {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    const data = {
      labels: distinct.map((item) => item.brand),
      datasets: [
        {
          label: "Stock by Brand",
          data: distinct.map((item) => item.quantity),
          backgroundColor: distinct.map((_, index) => {
            const colors = [
              "rgba(255, 159, 64, 0.2)",
              "rgba(75, 192, 192, 0.2)",
              "rgba(54, 162, 235, 0.2)",
              "rgba(153, 102, 255, 0.2)",
              "rgba(255, 206, 86, 0.2)",
              "rgba(231, 233, 237, 0.2)",
            ];
            return colors[index % colors.length];
          }),
          borderColor: distinct.map((_, index) => {
            const colors = [
              "rgb(255, 159, 64)",
              "rgb(75, 192, 192)",
              "rgb(54, 162, 235)",
              "rgb(153, 102, 255)",
              "rgb(255, 206, 86)",
              "rgb(231, 233, 237)",
            ];
            return colors[index % colors.length];
          }),
          borderWidth: 1,
        },
      ],
    };
    const options = {
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [distinct]);

  return (
    <>
      <div className="bg-white w-2/3 rounded-lg overflow-hidden m-5">
        <div className="bg-red-500 h-9 p-2 flex items-center">
          Model Wise Stock
        </div>
        <Chart
          type="bar"
          data={chartData}
          options={chartOptions}
          className="p-4 w-11/12"
        />
      </div>

      <div className="bg-white w-1/3 rounded-lg overflow-hidden m-5 flex flex-col">
        <div className="bg-red-500 h-9 p-2 flex items-center">
          Condition Wise Stock
        </div>
        <PieChart conditionDistinct={conditionDistinct} />
      </div>
    </>
  );
}

function PieChart({ conditionDistinct }) {
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    if (conditionDistinct && conditionDistinct.length > 0) {
      const data = {
        labels: conditionDistinct.map((item) => item.condition),
        datasets: [
          {
            data: conditionDistinct.map((item) => item.quantity),
            backgroundColor: [
              "rgb(255, 255, 0)",
              "rgb(169, 169, 169)",
              "rgb(211, 211, 211)",
            ],
            hoverBackgroundColor: [
              "rgb(54, 162, 235)",
              "rgb(255, 128, 0)",
              "rgb(75, 192, 192)",
            ],
          },
        ],
      };
      const options = {
        plugins: {
          legend: {
            labels: {
              usePointStyle: true,
            },
          },
        },
      };

      setChartData(data);
      setChartOptions(options);
    }
  }, [conditionDistinct]);

  return (
    <div className="w-full h-full">
      {chartData.labels && chartData.datasets ? (
        <Chart
          type="pie"
          data={chartData}
          options={chartOptions}
          className="w-full md:w-30rem"
        />
      ) : (
        <p>No data available for the pie chart.</p>
      )}
    </div>
  );
}

function SummaryCard({ logo, title, num, color }) {
  return (
    <div
      className={
        "h-auto py-6 px-8 rounded-lg flex-shrink-0 flex-col items-center justify-center text-center m-2"
      }
      style={{ background: `${color}` }}
    >
      <img src={logo} alt="img.png" className={"w-24 mix-blend-multiply"} />
      {title} <br />
      {num}
    </div>
  );
}
